// Which "add to home screen" path a browser offers. Only Chromium browsers expose a one-tap
// prompt (`beforeinstallprompt`); Safari/WebKit has no API, so iOS gets guided steps.
export type InstallPath =
  | 'installed' // already running as the home-screen app
  | 'native' // one tap: our button opens the browser's own install dialog
  | 'ios-safari' // Share → Add to Home Screen
  | 'ios-browser' // Chrome/Firefox/Edge on iOS 16.4+: same steps from their share button
  | 'in-app' // in-app browser (Instagram, Facebook, …): open in Safari/Chrome first
  | 'android-manual' // Android browser that did not fire the prompt: use its menu
  | 'unsupported';

const IN_APP = /FBAN|FBAV|FB_IAB|Instagram|Line\/|LinkedInApp|Twitter|TikTok|Snapchat|Pinterest|GSA\/|; wv\)/i;

export function isIos(userAgent: string, maxTouchPoints = 0): boolean {
  // iPadOS reports a Mac user agent; touch support tells them apart.
  return /iPad|iPhone|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
}

/**
 * Açılır pencereye güvenilmeyen gömülü tarayıcı: uygulama içi tarayıcılar ve iOS uygulama
 * WebView'ı (WKWebView kimliğinde `Safari/` yoktur; ana ekran uygulaması `standalone` ile ayrılır).
 */
export function isEmbeddedBrowser(userAgent: string, maxTouchPoints = 0, standalone = false): boolean {
  if (IN_APP.test(userAgent)) return true;
  return isIos(userAgent, maxTouchPoints) && !standalone && !/Safari\//.test(userAgent);
}

export function installPath({
  userAgent,
  maxTouchPoints = 0,
  standalone,
  hasPrompt,
}: {
  userAgent: string;
  maxTouchPoints?: number;
  standalone: boolean;
  hasPrompt: boolean;
}): InstallPath {
  if (standalone) return 'installed';
  if (hasPrompt) return 'native';
  if (IN_APP.test(userAgent)) return 'in-app';
  if (isIos(userAgent, maxTouchPoints)) {
    return /CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent) ? 'ios-browser' : 'ios-safari';
  }
  if (/Android/i.test(userAgent)) return 'android-manual';
  return 'unsupported';
}

export const INSTALL_DISMISS_DAYS = 30;

/** The banner waits for real use (second visit or signed in) and respects a 30-day dismissal. */
export function shouldOfferInstall({
  path,
  isPhone,
  visits,
  signedIn,
  dismissedAt,
  now = Date.now(),
}: {
  path: InstallPath;
  isPhone: boolean;
  visits: number;
  signedIn: boolean;
  dismissedAt: number | null;
  now?: number;
}): boolean {
  if (!isPhone || path === 'installed' || path === 'unsupported') return false;
  if (dismissedAt && now - dismissedAt < INSTALL_DISMISS_DAYS * 86400000) return false;
  return signedIn || visits >= 2;
}
